import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useRef, useState, type FormEvent } from 'react';
import {
  apiAdminGetUploadUrls,
  apiAdminUploadTriggerIngestion,
} from '../data/api';
import {
  INGESTION_STATUSES,
  isTerminalIngestionStatus,
  type IngestionStatus,
} from '../data/ingestion';
import { uploadFileToS3 } from '../data/s3_upload';
import { useIngestionStatus } from '../hooks/use_ingestion_status';
import { prepareSignedUploadRequest } from '../lib/upload_preparation';

interface UploadDataPageProps {
  onSignOut?: () => void;
}

type UploadPhase =
  | 'idle'
  | 'validation'
  | 'requesting-urls'
  | 'uploading-csv'
  | 'uploading-zip'
  | 'starting'
  | 'backend'
  | 'reconnecting'
  | 'completed'
  | 'failed';

const BACKEND_STATUSES = INGESTION_STATUSES.filter(
  (status) => status !== 'DONE' && status !== 'FAILED',
);

const STATUS_LABELS: Record<IngestionStatus, string> = {
  CREATED: 'Created',
  AWAITING_UPLOAD: 'Awaiting upload',
  STARTING: 'Starting ingestion',
  RETRIEVING_FILES: 'Retrieving files',
  PARSING_CSV: 'Parsing CSV',
  EXTRACTING_IMAGES: 'Extracting images',
  GENERATING_ASSETS: 'Generating assets',
  DONE: 'Completed',
  FAILED: 'Failed',
};

function hasExtension(file: File, extension: string) {
  return file.name.toLowerCase().endsWith(extension);
}

function formatUpdatedAt(value: string): string {
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) {
    return 'Last update unavailable';
  }

  return `Last updated ${new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(timestamp)}`;
}

function getPhaseLabel(
  phase: UploadPhase,
  status: IngestionStatus | undefined,
): string {
  switch (phase) {
    case 'validation':
      return 'Checking files…';
    case 'requesting-urls':
      return 'Requesting secure upload URLs…';
    case 'uploading-csv':
      return 'Uploading CSV to S3…';
    case 'uploading-zip':
      return 'Uploading ZIP to S3…';
    case 'starting':
      return 'Starting ingestion…';
    case 'backend':
      return status ? `Ingestion stage: ${STATUS_LABELS[status]}` : 'Ingestion is processing…';
    case 'reconnecting':
      return 'Reconnecting to ingestion updates…';
    case 'completed':
      return 'Ingestion completed.';
    case 'failed':
      return 'Ingestion failed.';
    default:
      return 'Choose both files to begin.';
  }
}

function getUploadFailureMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    const message = error.message.trim();
    if (!/bearer|authorization|access.?token|x-amz-|signature/i.test(message)) {
      return `The upload could not be completed: ${message.slice(0, 240)}`;
    }
  }

  return 'The upload could not be completed. Check the files and try again.';
}

export function UploadDataPage({ onSignOut }: UploadDataPageProps) {
  const [csvFile, setCsvFile] = useState<File>();
  const [zipFile, setZipFile] = useState<File>();
  const [csvProgress, setCsvProgress] = useState(0);
  const [zipProgress, setZipProgress] = useState(0);
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [error, setError] = useState('');
  const [canRetry, setCanRetry] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { getAccessTokenSilently } = useAuth0();
  const getAccessToken = useCallback(
    () => getAccessTokenSilently(),
    [getAccessTokenSilently],
  );
  const ingestion = useIngestionStatus({ getAccessToken });
  const backendStatus = ingestion.job?.status;
  const isTerminal = backendStatus
    ? isTerminalIngestionStatus(backendStatus)
    : phase === 'completed';
  const isActive =
    !isTerminal &&
    (Boolean(ingestion.activeJobId) || (phase !== 'idle' && phase !== 'failed'));
  const phaseFromJob: UploadPhase | undefined =
    backendStatus === 'DONE'
      ? 'completed'
      : backendStatus === 'FAILED'
        ? 'failed'
        : ingestion.activeJobId && phase === 'idle'
          ? 'backend'
          : undefined;
  const displayedPhase =
    ingestion.connectionState === 'reconnecting' && ingestion.activeJobId && !isTerminal
      ? 'reconnecting'
      : phaseFromJob ?? phase;

  const resetUpload = () => {
    ingestion.reset();
    setCsvFile(undefined);
    setZipFile(undefined);
    setCsvProgress(0);
    setZipProgress(0);
    setPhase('idle');
    setError('');
    setCanRetry(false);
    formRef.current?.reset();
  };

  const handleSignOut = () => {
    ingestion.reset();
    onSignOut?.();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setCanRetry(false);
    setPhase('validation');

    if (!csvFile || !zipFile) {
      setError('Select both a CSV data file and a ZIP image archive.');
      setPhase('failed');
      setCanRetry(true);
      return;
    }

    if (!hasExtension(csvFile, '.csv')) {
      setError('The athlete data file must use the .csv extension.');
      setPhase('failed');
      setCanRetry(true);
      return;
    }

    if (!hasExtension(zipFile, '.zip')) {
      setError('The image archive must use the .zip extension.');
      setPhase('failed');
      setCanRetry(true);
      return;
    }

    try {
      prepareSignedUploadRequest(csvFile, zipFile);
      setPhase('requesting-urls');
      const accessToken = await getAccessToken();
      const uploadUrlResponse = await apiAdminGetUploadUrls(accessToken);

      setCsvProgress(0);
      setPhase('uploading-csv');
      await uploadFileToS3(csvFile, uploadUrlResponse.csv.url, setCsvProgress);

      setZipProgress(0);
      setPhase('uploading-zip');
      await uploadFileToS3(zipFile, uploadUrlResponse.zip.url, setZipProgress);

      setPhase('starting');
      await apiAdminUploadTriggerIngestion(uploadUrlResponse.jobId, accessToken);

      ingestion.trackJob(uploadUrlResponse.jobId);
      setPhase('backend');
      setCanRetry(false);
      setCsvFile(undefined);
      setZipFile(undefined);
      formRef.current?.reset();
    } catch (uploadError) {
      setError(getUploadFailureMessage(uploadError));
      setPhase('failed');
      setCanRetry(true);
    }
  };

  const phaseLabel = getPhaseLabel(displayedPhase, backendStatus);
  const showBackendStages = Boolean(ingestion.activeJobId);
  const disableInputs = isActive || isTerminal;
  const disableSubmit = !csvFile || !zipFile || isActive || isTerminal;
  const displayedError =
    error ||
    (backendStatus === 'FAILED'
      ? ingestion.error || 'The ingestion failed. Start a new upload to try again.'
      : '');

  return (
    <div className="app-gradient min-h-[calc(100dvh-3.5rem)] py-12">
      <section className="mx-auto w-full max-w-3xl px-6">
        <div className="mb-7 flex items-start justify-between gap-6">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-qs-indigo">
              Secure ingestion
            </p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-qs-navy sm:text-4xl">
              Upload event data
            </h1>
            <p className="mt-3 max-w-2xl leading-7 text-aws-slate">
              Select the athlete CSV and its matching image archive. Upload progress and ingestion
              stages will appear here.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <a
              href="/admin/results"
              className="button-primary px-4 py-2 text-center text-sm font-black"
            >
              Manage results
            </a>
          {onSignOut && (
            <button
              type="button"
              className="rounded-lg border border-qs-indigo px-4 py-2 text-sm font-black text-qs-indigo transition hover:bg-qs-lilac"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          )}
          </div>
        </div>

        <form
          ref={formRef}
          className="surface-card space-y-6 p-6 sm:p-8"
          noValidate
          onSubmit={(submitEvent) => {
            void handleSubmit(submitEvent);
          }}
        >
          <div>
            <label htmlFor="athlete-data" className="block text-base font-black text-aws-ink">
              Athlete data
            </label>
            <p id="athlete-data-help" className="mt-1 text-sm text-aws-slate">
              Choose one CSV file containing the event results.
            </p>
            <input
              id="athlete-data"
              name="athleteData"
              type="file"
              accept=".csv,text/csv"
              required
              disabled={disableInputs}
              aria-describedby="athlete-data-help"
              className="input-themed mt-3 block w-full cursor-pointer p-3 text-sm text-aws-slate disabled:cursor-not-allowed disabled:opacity-60 file:mr-4 file:rounded-lg file:border-0 file:bg-qs-lilac file:px-4 file:py-2 file:font-black file:text-qs-indigo"
              onChange={(changeEvent) => setCsvFile(changeEvent.target.files?.[0])}
            />
            {csvFile && (
              <p className="mt-2 text-sm text-aws-slate" aria-label="Selected CSV file">
                {csvFile.name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="athlete-images" className="block text-base font-black text-aws-ink">
              Athlete images
            </label>
            <p id="athlete-images-help" className="mt-1 text-sm text-aws-slate">
              Choose one ZIP archive containing the corresponding images.
            </p>
            <input
              id="athlete-images"
              name="athleteImages"
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              required
              disabled={disableInputs}
              aria-describedby="athlete-images-help"
              className="input-themed mt-3 block w-full cursor-pointer p-3 text-sm text-aws-slate disabled:cursor-not-allowed disabled:opacity-60 file:mr-4 file:rounded-lg file:border-0 file:bg-qs-lilac file:px-4 file:py-2 file:font-black file:text-qs-indigo"
              onChange={(changeEvent) => setZipFile(changeEvent.target.files?.[0])}
            />
            {zipFile && (
              <p className="mt-2 text-sm text-aws-slate" aria-label="Selected ZIP file">
                {zipFile.name}
              </p>
            )}
          </div>

          <section
            className="rounded-xl border border-qs-lilac bg-white/70 p-4 sm:p-5"
            role="region"
            aria-label="Upload and ingestion status"
            aria-live="polite"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-black text-aws-ink">Upload status</h2>
                <p className="mt-1 text-sm font-bold text-qs-indigo" role="status">
                  {phaseLabel}
                </p>
              </div>
              {ingestion.activeJobId && (
                <p className="text-right text-xs text-aws-slate">Job status is saved for refresh.</p>
              )}
            </div>

            {(displayedPhase === 'uploading-csv' || csvProgress > 0) && (
              <div className="mt-4">
                <div className="flex justify-between text-sm font-bold text-aws-slate">
                  <span>CSV upload</span>
                  <span>{csvProgress}%</span>
                </div>
                <progress
                  className="mt-2 h-3 w-full accent-qs-indigo"
                  max="100"
                  value={csvProgress}
                  aria-label="CSV upload progress"
                />
              </div>
            )}

            {(displayedPhase === 'uploading-zip' || zipProgress > 0) && (
              <div className="mt-4">
                <div className="flex justify-between text-sm font-bold text-aws-slate">
                  <span>ZIP upload</span>
                  <span>{zipProgress}%</span>
                </div>
                <progress
                  className="mt-2 h-3 w-full accent-qs-indigo"
                  max="100"
                  value={zipProgress}
                  aria-label="ZIP upload progress"
                />
              </div>
            )}

            {showBackendStages && (
              <ol
                className="mt-5 grid gap-2 sm:grid-cols-2"
                aria-label="Backend ingestion stages"
              >
                {BACKEND_STATUSES.map((status) => {
                  const backendStage =
                    backendStatus && backendStatus !== 'DONE' && backendStatus !== 'FAILED'
                      ? backendStatus
                      : undefined;
                  const currentIndex = backendStage
                    ? BACKEND_STATUSES.indexOf(backendStage)
                    : -1;
                  const stageIndex = BACKEND_STATUSES.indexOf(status);
                  const isCurrent = backendStage === status;
                  const isComplete =
                    backendStatus === 'DONE' || (currentIndex >= 0 && stageIndex < currentIndex);

                  return (
                    <li
                      key={status}
                      className={`rounded-lg px-3 py-2 text-sm ${
                        isCurrent
                          ? 'bg-qs-indigo font-black text-white'
                          : isComplete
                            ? 'bg-qs-lilac font-bold text-qs-indigo'
                            : 'bg-slate-50 text-aws-slate'
                      }`}
                      aria-current={isCurrent ? 'step' : undefined}
                    >
                      {isComplete ? '✓ ' : ''}
                      {STATUS_LABELS[status]}
                    </li>
                  );
                })}
              </ol>
            )}

            {backendStatus === 'GENERATING_ASSETS' && ingestion.job && (
              <div className="mt-4">
                <div className="flex justify-between text-sm font-bold text-aws-slate">
                  <span>Asset generation</span>
                  <span>
                    {ingestion.job.assetsCompleted} of {ingestion.job.assetsTotal} athletes
                  </span>
                </div>
                <progress
                  className="mt-2 h-3 w-full accent-qs-indigo"
                  max={Math.max(ingestion.job.assetsTotal, 1)}
                  value={ingestion.job.assetsCompleted}
                  aria-label="Athlete asset generation progress"
                />
              </div>
            )}

            {ingestion.job && (
              <p className="mt-4 text-xs text-aws-slate">
                {formatUpdatedAt(ingestion.job.updatedAt)}
                {ingestion.job.completedAt ? ` · Completed ${formatUpdatedAt(ingestion.job.completedAt).replace('Last updated ', '')}` : ''}
              </p>
            )}

            {displayedError && (
              <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-800">
                {displayedError}
              </p>
            )}
            {!displayedError && ingestion.error && displayedPhase !== 'failed' && (
              <p role="status" className="mt-4 text-sm font-bold text-aws-slate">
                {ingestion.error}
              </p>
            )}
          </section>

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              className="button-primary inline-flex min-h-12 items-center justify-center px-6 py-3 font-black disabled:cursor-not-allowed disabled:opacity-50"
              disabled={disableSubmit}
            >
              {isActive ? phaseLabel : canRetry ? 'Retry upload' : 'Upload and process'}
            </button>
            {isTerminal && (
              <button
                type="button"
                className="inline-flex min-h-12 items-center justify-center rounded-lg border border-qs-indigo px-6 py-3 font-black text-qs-indigo transition hover:bg-qs-lilac"
                onClick={resetUpload}
              >
                Start a new upload
              </button>
            )}
            {ingestion.activeJobId && !isTerminal && (
              <button
                type="button"
                className="inline-flex min-h-12 items-center justify-center rounded-lg border border-aws-slate px-6 py-3 font-black text-aws-slate transition hover:bg-slate-50"
                onClick={resetUpload}
              >
                Cancel status updates
              </button>
            )}
            {canRetry && !isTerminal && (
              <button
                type="button"
                className="inline-flex min-h-12 items-center justify-center rounded-lg border border-aws-slate px-6 py-3 font-black text-aws-slate transition hover:bg-slate-50"
                onClick={resetUpload}
              >
                Clear files
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
